import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { ArrowSquareOut, FileText, PencilSimple } from "@phosphor-icons/react"
import { ListItem, ListText } from "@/components/ui/list"
import { Tag } from "@/components/ui/tag"
import { cn } from "@/lib/utils"
import { ChGeneralAttestationDrawer } from "@/modules/patients/detail/general-attestation/ch/ch-general-attestation-drawer"
import { chGeneralAttestationsFragment$key } from "@/modules/patients/detail/general-attestation/ch/services/__generated__/chGeneralAttestationsFragment.graphql"
import { chGeneralAttestationsFragment } from "@/modules/patients/detail/general-attestation/ch/services/chGeneralAttestationsFragment"
import { DeGeneralAttestationDrawer } from "@/modules/patients/detail/general-attestation/de/de-general-attestation-drawer"
import { deGeneralAttestationsFragment$key } from "@/modules/patients/detail/general-attestation/de/services/__generated__/deGeneralAttestationsFragment.graphql"
import { deGeneralAttestationsFragment } from "@/modules/patients/detail/general-attestation/de/services/deGeneralAttestationsFragment"
import { useGeneralAttestationContext } from "@/modules/patients/detail/general-attestation/store/useGeneralAttestationStore"
import { DocumentProps } from "@/modules/patients/detail/progress-note/types/document"
import { useConcludeDialogStoreContext } from "@/modules/patients/detail/store/useConcludeDialogStore"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { useDrawerStore } from "@/stores/useDrawerStore"

interface GeneralAttestationDocumentProps extends DocumentProps {
  generalAttestationRef: deGeneralAttestationsFragment$key | chGeneralAttestationsFragment$key
  isEditable?: boolean
}
const GeneralAttestationDocument = ({
  generalAttestationRef,
  isEditable = true,
  isConclusion,
  isSigned,
  isSent,
  onSelect,
}: GeneralAttestationDocumentProps) => {
  const [t] = useTranslation()
  const country = useCountryParam()
  const generalAttestationsFragment = country === "DE" ? deGeneralAttestationsFragment : chGeneralAttestationsFragment
  const { id, name, subject, progressNoteId } = useFragment(generalAttestationsFragment, generalAttestationRef) ?? {}

  const setGeneralAttestationId = useGeneralAttestationContext((state) => state.setGeneralAttestationId)
  const setProgressNoteId = useGeneralAttestationContext((state) => state.setProgressNoteId)

  const selectedDocumentId = useConcludeDialogStoreContext((state) => state.selectedDocument?.id)
  const openDrawer = useDrawerStore((state) => state.openDrawer)
  const isSelected = isConclusion && selectedDocumentId === id

  const triggeredValidation = useConcludeDialogStoreContext((state) => state.triggeredValidation)

  return (
    <ListItem
      className={cn(
        "group hover:bg-surface-hovered flex h-12 items-center hover:cursor-pointer",
        isSelected && "bg-primary-surface hover:bg-primary-surface-hovered active:bg-primary-surface-pressed",
        !isEditable && !isConclusion && !isSent && "cursor-auto!",
      )}
      data-testid="general-attestation-document-item"
      onClick={() => {
        if (!isEditable) {
          onSelect()
          return
        }
        openDrawer(
          () => (country === "DE" ? <DeGeneralAttestationDrawer /> : <ChGeneralAttestationDrawer />),
          "GeneralAttestationDrawer",
        )
        setProgressNoteId(progressNoteId)
        setGeneralAttestationId(id, generalAttestationRef)
      }}>
      <FileText className={cn("shrink-0", isSelected ? "text-on-primary-surface" : "text-on-surface-subdued")} />
      <div className={cn("ml-2 min-w-0", isEditable && "group-hover:mr-4")}>
        <ListText
          data-testid="summary-general-attestation-header"
          className={isSelected ? "text-on-primary-surface" : ""}>
          {t("episodeOfCare.progressNote.generalAttestation")}
        </ListText>
        <ListText
          data-testid="general-attestation-details"
          variant="secondary"
          className={cn("truncate", isSelected && "text-on-primary-surface")}>{`${name} • ${subject}`}</ListText>
      </div>
      {isEditable && (
        <div className="ml-auto hidden group-hover:block">
          <div className="hover:bg-surface-hovered p-2 hover:rounded-full hover:p-2">
            <PencilSimple className="text-on-surface-subdued" data-testid="general-attestation-edit-button" />
          </div>
        </div>
      )}
      {!isConclusion && isSent && (
        <div className="ml-auto hidden group-hover:block">
          <div className="hover:bg-surface-hovered p-2 hover:rounded-full hover:p-2">
            <ArrowSquareOut
              className="text-on-surface-subdued"
              data-testid="general-attestation-view-document-button"
            />
          </div>
        </div>
      )}
      {isConclusion && (triggeredValidation || isSigned) && (
        <Tag
          className="ml-auto group-hover:hidden"
          type={isSigned ? "success" : "critical"}
          label={isSigned ? t("patient.conclude.signed") : t("patient.conclude.signThis")}
        />
      )}
    </ListItem>
  )
}

export { GeneralAttestationDocument }
